// Ranked search for the command menu (Cmd/Ctrl + K).
//
// cmdk's built-in filter is a loose subsequence matcher, so "hello" matched pages like
// "cHangE management … Logs". This scorer only matches on word boundaries and ranks results:
//   title  > alias/keyword > parent section
//   exact  > starts with > whole-word phrase > every word prefixed > acronym > substring > typo

type FieldKind = 'title' | 'alias' | 'section'

const FIELD_WEIGHT: Record<FieldKind, number> = { title: 1, alias: 0.8, section: 0.4 }

// Filler words ignored when the query also has meaningful words ("context of the organisation")
const STOP_WORDS = new Set(['a', 'an', 'and', 'the', 'of', 'to', 'for', 'in', 'on', 'my', 'go', 'open', 'page'])

export interface CommandSearchFields {
  title: string
  aliases?: string[]
  sections?: string[]
}

export const normaliseSearch = (text: string) =>
  text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()

const toWords = (text: string) => normaliseSearch(text).split(' ').filter(Boolean)

export const getSearchTerms = (search: string): string[] => {
  const terms = toWords(search)
  const meaningful = terms.filter((term) => !STOP_WORDS.has(term))
  return meaningful.length ? meaningful : terms
}

// Encode the searchable fields into cmdk `keywords` so the filter knows which field matched
export const buildSearchKeywords = ({ title, aliases = [], sections = [] }: CommandSearchFields): string[] => [
  `title:${title}`,
  ...aliases.map((alias) => `alias:${alias}`),
  ...sections.map((section) => `section:${section}`),
]

const parseKeywords = (keywords: string[]) =>
  keywords.flatMap((keyword) => {
    const separator = keyword.indexOf(':')
    const kind = keyword.slice(0, separator) as FieldKind
    if (!(kind in FIELD_WEIGHT)) return []
    return [{ kind, words: toWords(keyword.slice(separator + 1)) }]
  })

const editDistance = (a: string, b: string): number => {
  let prev = Array.from({ length: b.length + 1 }, (_, i) => i)
  for (let i = 1; i <= a.length; i++) {
    const curr = [i]
    for (let j = 1; j <= b.length; j++)
      curr[j] = Math.min(prev[j] + 1, curr[j - 1] + 1, prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1))
    prev = curr
  }
  return prev[b.length]
}

// Tolerate a typo in 4-6 letter words and two in longer ones ("polcy" -> "policy", "regiiister" -> "register").
// Short words must be spelled correctly, otherwise nearly everything matches.
const isTypoMatch = (term: string, word: string): boolean => {
  if (term.length < 4 || /\d/.test(term)) return false
  const maxTypos = term.length <= 6 ? 1 : 2
  if (Math.abs(term.length - word.length) <= maxTypos && editDistance(term, word) <= maxTypos) return true
  // Typo in a partially typed word ("vulnra" -> "vulnerability")
  return term.length >= 5 && word.length > term.length && editDistance(term, word.slice(0, term.length)) <= 1
}

// Each term must match a different word, so "risk risk" doesn't match "Risk Register" twice
const matchAllTerms = (terms: string[], words: string[], matches: (term: string, word: string) => boolean) => {
  const used = new Set<number>()
  return terms.every((term) => {
    const index = words.findIndex((word, i) => !used.has(i) && matches(term, word))
    if (index === -1) return false
    used.add(index)
    return true
  })
}

const scoreField = (terms: string[], words: string[]): number => {
  if (!words.length) return 0
  const query = terms.join(' ')
  const text = words.join(' ')
  const compactQuery = terms.join('')

  if (text === query) return 1
  if (text.startsWith(query)) return 0.9
  if (` ${text}`.includes(` ${query}`)) return 0.8
  if (matchAllTerms(terms, words, (term, word) => word.startsWith(term))) return 0.7
  // Acronyms: "cm" -> Change Management, "iac" -> ISMS Assurance Calendar
  if (compactQuery.length >= 2 && words.length >= 2 && words.map((word) => word[0]).join('') === compactQuery)
    return 0.65
  if (compactQuery.length >= 3 && text.replace(/ /g, '').includes(compactQuery)) return 0.35
  if (matchAllTerms(terms, words, (term, word) => word.startsWith(term) || isTypoMatch(term, word))) return 0.2
  return 0
}

// Returns 0 (hidden) to 1 (best match); plugs straight into cmdk's `filter` prop
export const commandMenuFilter = (_value: string, search: string, keywords: string[] = []): number => {
  const terms = getSearchTerms(search)
  if (!terms.length) return 1

  const fields = parseKeywords(keywords)
  let best = 0
  for (const field of fields) best = Math.max(best, scoreField(terms, field.words) * FIELD_WEIGHT[field.kind])

  // Terms spread across fields: "governance policy" -> Policy (in the Governance section)
  if (!best && terms.length > 1) {
    const allWords = fields.flatMap((field) => field.words)
    if (matchAllTerms(terms, allWords, (term, word) => word.startsWith(term))) best = 0.3
    else if (matchAllTerms(terms, allWords, (term, word) => word.startsWith(term) || isTypoMatch(term, word)))
      best = 0.1
  }
  return best
}

// Split a title into plain/highlighted parts for the words the search terms prefix-match
export const highlightMatches = (title: string, search: string): { text: string; match: boolean }[] => {
  const terms = getSearchTerms(search)
  if (!terms.length) return [{ text: title, match: false }]

  return title.split(/(\s+)/).flatMap((segment) => {
    const lower = segment.toLowerCase()
    const term = terms.filter((candidate) => lower.startsWith(candidate)).sort((a, b) => b.length - a.length)[0]
    if (!term) return [{ text: segment, match: false }]
    const rest = segment.slice(term.length)
    return rest
      ? [
          { text: segment.slice(0, term.length), match: true },
          { text: rest, match: false },
        ]
      : [{ text: segment, match: true }]
  })
}
